import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { School, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingSkeleton } from '../../components/shared/LoadingSkeleton';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { Link } from 'react-router-dom';

interface Class {
  id: number;
  name: string;
  display_name: string;
  grade_number: number;
  status: string;
}

export default function ClassesPage() {
  const queryClient = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['classes'],
    queryFn: async () => api.get<any>('/api/classes'),
  });

  const classes: Class[] = data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => api.delete(`/api/classes/${id}`),
    onSuccess: () => {
      toast.success('Class deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete class');
    },
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        icon={School}
        iconColor="text-tangerine-500"
        title="Classes"
        subtitle="Manage CBSE classes VI-XII"
        actionTo="/classes/create"
        actionLabel="Add Class"
      />

      {isLoading ? (
        <LoadingSkeleton count={6} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
          {classes.map((cls, index) => (
            <motion.div
              key={cls.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="glass-card p-3 sm:p-5 rounded-2xl hover:shadow-lg transition-all"
            >
              <div className="flex items-start justify-between mb-2 sm:mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl gradient-tangerine flex items-center justify-center">
                  <School className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="flex items-center gap-1">
                  <Link to={`/classes/${cls.id}/edit`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-light-text-muted hover:text-rose-500"
                    onClick={() => setDeleteConfirm(cls.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-light-text dark:text-dark-text">{cls.name}</h3>
              <p className="text-xs sm:text-sm text-light-text-muted">{cls.display_name}</p>
              <div className="mt-2 sm:mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] sm:text-xs px-2 py-1 rounded-full bg-tangerine-100 dark:bg-tangerine-500/20 text-tangerine-700 dark:text-tangerine-300">
                  Grade {cls.grade_number}
                </span>
                <span className={`text-[10px] sm:text-xs px-2 py-1 rounded-full ${cls.status === 'active' ? 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300' : 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300'}`}>
                  {cls.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Class"
        onConfirm={() => deleteMutation.mutate(deleteConfirm!)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
